/**
 * 🗺️ CROSSINGS TAB - Mode Mozart/Jobs/Tesla
 * 
 * Destins croisés - Les gens que tu as croisés IRL
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation, Heart, X, MessageCircle, Sparkles, AlertCircle, Monitor } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { api } from '../../../services/api';
import { toast } from 'sonner';

// Import des composants existants
import { CrossingsPanel, CrossingsMap } from '../components';

function CrossingsTab({ customerId, isDarkMode, profile }) {
  const [crossings, setCrossings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [geoStatus, setGeoStatus] = useState('unknown'); // 'unknown', 'requesting', 'granted', 'denied', 'unavailable'
  const [showManualLocation, setShowManualLocation] = useState(false);
  const [manualCity, setManualCity] = useState('');

  useEffect(() => {
    loadCrossings();
    checkGeoPermission();
  }, [customerId]);

  // Vérifier si la géolocalisation est disponible
  const checkGeoPermission = async () => {
    if (!navigator.geolocation) {
      setGeoStatus('unavailable');
      return;
    }
    
    // Sur desktop, vérifier si les permissions sont supportées
    if (navigator.permissions) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setGeoStatus(result.state); // 'granted', 'denied', 'prompt'
        result.onchange = () => setGeoStatus(result.state);
      } catch (e) {
        // Firefox peut ne pas supporter cette query
        setGeoStatus('unknown');
      }
    }
  };

  const loadCrossings = async () => {
    try {
      setLoading(true);
      const data = await api.getCrossings(customerId);
      setCrossings(data?.crossings || []);
    } catch (error) {
      console.error('Erreur chargement destins croisés:', error);
    } finally {
      setLoading(false);
    }
  };

  // Activer la géolocalisation
  const activateGeolocation = () => {
    if (!navigator.geolocation) {
      setGeoStatus('unavailable');
      toast.error('📍 Géolocalisation non disponible sur cet appareil');
      setShowManualLocation(true);
      return;
    }

    setGeoStatus('requesting');
    toast.info('📍 Demande de localisation en cours...');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Sauvegarder la position sur le serveur
          await api.updateLocation(customerId, position.coords.latitude, position.coords.longitude);
          await api.updateHeartbeatsProfile(customerId, { locationEnabled: true });
          setGeoStatus('granted');
          toast.success('📍 Localisation activée ! On te prévient quand tu croises quelqu\'un.');
          loadCrossings();
        } catch (error) {
          toast.error('Erreur lors de la sauvegarde de la position');
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setGeoStatus('denied');
        
        // Messages d'erreur adaptés
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('📍 Accès refusé. Autorise la localisation dans les paramètres de ton navigateur.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('📍 Position non disponible. Es-tu sur un ordinateur de bureau ?');
            setShowManualLocation(true);
            break;
          case error.TIMEOUT:
            toast.error('📍 Délai dépassé. Réessaie.');
            break;
          default:
            toast.error('📍 Erreur de localisation');
        }
      },
      {
        enableHighAccuracy: false, // false = plus rapide, moins précis mais OK pour desktop
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  // Sauvegarder une localisation manuelle (par ville)
  const saveManualLocation = async () => {
    if (!manualCity.trim()) {
      toast.error('Entre une ville ou une adresse');
      return;
    }

    try {
      // Utiliser une API de géocodage (ou simplement sauvegarder le nom)
      await api.updateHeartbeatsProfile(customerId, { 
        locationEnabled: true,
        manualLocation: manualCity.trim()
      });
      toast.success(`📍 Localisation définie : ${manualCity}`);
      setShowManualLocation(false);
      loadCrossings();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Envoyer un like/crush
  const handleLike = async (targetId) => {
    try {
      await api.sendCrush(customerId, targetId);
      toast.success('💜 Crush envoyé !');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (crossings.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <Navigation className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
          <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            🗺️ Destins Croisés
          </h3>
          <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Découvre les gens que tu croises au quotidien !
          </p>

          {/* Message spécial pour desktop */}
          {(geoStatus === 'unavailable' || geoStatus === 'denied') && (
            <div className={`mb-4 p-3 rounded-xl ${isDarkMode ? 'bg-amber-900/30 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <Monitor className="w-5 h-5 text-amber-500" />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  Tu es sur un ordinateur ?
                </span>
              </div>
              <p className={`text-xs ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600'}`}>
                La géolocalisation fonctionne mieux sur mobile. Tu peux définir ta ville manuellement.
              </p>
            </div>
          )}

          {/* Formulaire localisation manuelle */}
          {showManualLocation ? (
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Entre ta ville ou ton quartier :
              </p>
              <div className="flex gap-2">
                <Input
                  value={manualCity}
                  onChange={(e) => setManualCity(e.target.value)}
                  placeholder="Ex: Annecy, Paris 11e..."
                  className={isDarkMode ? 'bg-gray-700 border-gray-600' : ''}
                />
                <Button onClick={saveManualLocation} className="bg-purple-500">
                  OK
                </Button>
              </div>
              <button 
                onClick={() => setShowManualLocation(false)}
                className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
              >
                Annuler
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <Button
                onClick={activateGeolocation}
                disabled={geoStatus === 'requesting'}
                className="bg-gradient-to-r from-purple-500 to-pink-500"
              >
                <MapPin className="w-4 h-4 mr-2" />
                {geoStatus === 'requesting' ? 'Localisation...' : 'Activer la localisation'}
              </Button>
              
              <div>
                <button
                  onClick={() => setShowManualLocation(true)}
                  className={`text-sm underline ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'}`}
                >
                  Ou entre ta ville manuellement
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            🗺️ Destins Croisés
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {crossings.length} personne(s) croisée(s) récemment
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowMap(!showMap)}
        >
          <MapPin className="w-4 h-4 mr-1" />
          {showMap ? 'Liste' : 'Carte'}
        </Button>
      </div>

      {/* Vue carte ou liste */}
      {showMap ? (
        <CrossingsMap 
          crossings={crossings}
          isDarkMode={isDarkMode}
          onSelect={(crossing) => toast.info(`${crossing.displayName} croisé(e) !`)}
        />
      ) : (
        <div className="space-y-3">
          {crossings.map((crossing, idx) => (
            <motion.div
              key={crossing.customerId || idx}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: idx * 0.1 }}
              className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
            >
              <div className="flex items-center gap-4">
                {/* Avatar */}
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center overflow-hidden">
                  {crossing.avatarImage ? (
                    <img src={crossing.avatarImage} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl">{crossing.avatar || '👤'}</span>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1">
                  <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {crossing.displayName}
                  </h4>
                  <p className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <MapPin className="w-3 h-3" />
                    Croisé(e) {crossing.location || 'récemment'}
                  </p>
                  {crossing.crossCount > 1 && (
                    <p className="text-xs text-purple-500 mt-1">
                      <Sparkles className="w-3 h-3 inline mr-1" />
                      {crossing.crossCount}x croisé(e) !
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleLike(crossing.customerId)}
                    className={isDarkMode ? 'border-gray-600' : ''}
                  >
                    <Heart className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CrossingsTab;
